import {
  Autocomplete,
  Checkbox,
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  deleteIssueWatcher,
  getIssueWatchers,
  saveIssueWatchers,
} from '../../../../../packages/common/api';
import CheckboxIconChecked from '../../../../../packages/common/shared-ui/src/icons/CheckboxIconChecked';
import ClearSelectChipIcon from '../../../../../packages/common/shared-ui/src/icons/ClearSelectChipIcon';
import DeleteIconBig from '../../../../../packages/common/shared-ui/src/icons/DeleteIconBig';
import PlusIcon from '../../../../../packages/common/shared-ui/src/icons/PlusIcon';
import SelectCheckboxIcon from '../../../../../packages/common/shared-ui/src/icons/SelectCheckboxIcon';
import SubmitIcon from '../../../../../packages/common/shared-ui/src/icons/SubmitIcon';
import { useTranslation } from '../../../../../packages/common/utils/getLocalString';

// Original class: filterSelect
const StyledFilterSelect = styled(Autocomplete)(theme => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-formControl': {
    backgroundColor: '#FFFFFF',
    width: '100%',
    fontSize: '14px',
    lineHeight: '20px',
    border: '1px solid #E4E4EF',
    padding: '8px 10px',
    height: 'auto',
    '&:hover': {
      border: '1px solid #E4E4EF',
      backgroundColor: '#FFFFFF',
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      padding: 0,
      border: 'none',
    },
    '& .MuiAutocomplete-input': {
      fontSize: '14px',
      lineHeight: '20px',
      color: '#7174AC',
      padding: '0',
      width: '100%',
      '&::placeholder': {
        fontSize: '12px',
        color: '#7174AC',
      },
    },
    '& .MuiButtonBase-root.MuiChip-root': {
      margin: 0,
      height: '30px',
      borderColor: '#E4E4EF',
      fontSize: '12px',
      lineHeight: '20px',
      '& .MuiChip-label': {
        padding: '0 26px 0 8px',
      },
      '& .MuiSvgIcon-root': {
        marginRight: '8px',
        width: '16px',
        height: '100%',
      },
      '& + .MuiButtonBase-root.MuiChip-root': {
        marginTop: '2px',
      },
    },
  },

  '&.MuiAutocomplete-hasClearIcon': {
    '& .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-formControl': {
      padding: '3px',
    },
  },

  '&.Mui-expanded': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      '& .MuiOutlinedInput-notchedOutline': {
        padding: 0,
      },
    },
  },

  '& + .MuiAutocomplete-popper': {
    minWidth: '180px!important',
    width: 'auto!important',
    maxWidth: '300px!important',
    '& .MuiPaper-root.MuiAutocomplete-paper': {
      boxShadow: '0px 5px 10px -8px #A0A1AA66',
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        '& li': {
          whiteSpace: ' nowrap',
          fontSize: '12px',
          lineHeight: '20px',
          color: '#212346',
          padding: '8px 10px',
          '&:hover, &.Mui-focused': {
            backgroundColor: '#DCDFF4',
          },
          '& .MuiButtonBase-root.MuiCheckbox-root': {
            padding: 0,
            marginRight: '10px',
          },
        },
        '&::-webkit-scrollbar': {
          width: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#7174AC',
          borderRadius: '5px',
        },
      },
    },
  },
}));
// Original class: addButton
const StyledAddButton = styled(IconButton)(theme => ({
  '& .MuiSvgIcon-root': {
    '& path': {
      transition: '.2s ease',
    },
  },

  '&:hover': {
    '& .MuiSvgIcon-root': {
      '& path': {
        fill: '#7070B8',
      },
    },
  },
}));
// Original class: hoveredIcon
const StyledHoveredIcon = styled(IconButton)(theme => ({
  '& .MuiSvgIcon-root': {
    '& path': {
      transition: '.2s ease',
    },
  },

  '&:hover': {
    '& .MuiSvgIcon-root': {
      '& path': {
        fill: '#7174AC',
      },
    },
  },
}));
const IssueWatchers = ({ permissions }) => {
  const getLocalString = useTranslation();
  const params = useParams();
  const [watchers, setWatchers] = useState([]);
  const [selectWatchers, setSelectWatchers] = useState([]);
  const [selectedWatchers, setSelectedWatchers] = useState([]);
  const [addWatcher, setAddWatcher] = useState(false);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let ignore = false;
    async function getWatchers() {
      const { data: watchers } = await getIssueWatchers(params.id);
      if (!ignore) {
        return watchers;
      }
    }
    getWatchers()
      .then(res => {
        if (res) {
          setWatchers(res.filter(el => el.isChecked));
          setSelectWatchers(res.filter(el => !el.isChecked));
        }
        setLoading(false);
      })
      .catch(error => console.error(error, 'ERROR IN GET'));
    return () => {
      ignore = true;
    };
  }, [params.id]);

  const handleSaveIssueWatchers = useCallback(async () => {
    if (selectedWatchers.length === 0) {
      return;
    }
    try {
      const response = await saveIssueWatchers(params.id, {
        users: selectedWatchers,
      });
      if (response) {
        const { data: watchers } = response;
        setWatchers(watchers.filter(el => el.isChecked));
        setAddWatcher(false);
      }
    } catch (error) {
      console.error('ERROR WITH SAVE', error);
    }
  }, [params.id, selectedWatchers]);

  const handleDeleteIssueWatcher = useCallback(
    async id => {
      try {
        const response = await deleteIssueWatcher(params.id, id);
        if (response) {
          const newArray = watchers.filter(watcher => watcher.userId !== id);
          setWatchers(newArray);
        }
      } catch (error) {
        console.error('ERROR WITH DELETE', error);
      }
    },
    [params.id, watchers],
  );

  const handleSelectWatchers = useCallback((event, value) => {
    setSelectedWatchers(value.map(el => el.userId));
  }, []);
  const handleAddWatcher = useCallback(() => {
    setAddWatcher(true);
  }, []);
  const handleRemoveWatcher = useCallback(() => {
    setAddWatcher(false);
    setSelectedWatchers([]);
  }, []);

  return (
    <>
      {loading ? (
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '70px',
          }}
        >
          <CircularProgress color='secondary' />
        </Grid>
      ) : (
        <>
          <Grid
            container
            sx={{
              alignItems: 'center',
            }}
          >
            <Grid>
              <Typography
                variant='h3'
                sx={{
                  color: '#082253',
                  fontWeight: 600,
                }}
              >
                {getLocalString('IssuePage', 'sections', 'watchers', 'title')}
              </Typography>
            </Grid>
            <Grid>
              {permissions.canAddWatchers && (
                <StyledAddButton
                  disabled={addWatcher}
                  onClick={handleAddWatcher}
                  sx={{ padding: '6px 4px' }}
                >
                  <PlusIcon width='24' height='24' viewBox='0 0 24 24' />
                </StyledAddButton>
              )}
            </Grid>
          </Grid>
          {addWatcher && (
            <Grid
              container
              sx={{
                alignItems: 'flex-start',
                marginTop: '10px',
              }}
            >
              <Grid>
                <StyledFilterSelect
                  disableClearable
                  forcePopupIcon={false}
                  disablePortal
                  multiple
                  options={selectWatchers}
                  disableCloseOnSelect
                  getOptionLabel={option => option.userName}
                  onChange={(event, newValue) =>
                    handleSelectWatchers(event, newValue)
                  }
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        checked={selected}
                        icon={<SelectCheckboxIcon />}
                        checkedIcon={
                          <CheckboxIconChecked
                            width='16'
                            height='16'
                            viewBox='0 0 16 16'
                          />
                        }
                      />
                      {option.userName}
                    </li>
                  )}
                  style={{ width: 240 }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      placeholder={getLocalString(
                        'IssuePage',
                        'sections',
                        'watchers',
                        'placeholder',
                      )}
                    />
                  )}
                  slotProps={{
                    chip: {
                      variant: 'outlined',
                      deleteIcon: <ClearSelectChipIcon />,
                    },
                  }}
                />
              </Grid>
              <Grid>
                <Grid
                  container
                  sx={{
                    marginLeft: '16px',
                  }}
                >
                  <Grid>
                    <IconButton
                      onClick={handleSaveIssueWatchers}
                      sx={{ padding: '8px', height: '100%' }}
                    >
                      <SubmitIcon width={'16px'} />
                    </IconButton>
                  </Grid>
                  <Grid>
                    {permissions.canDeleteWatchers && (
                      <StyledHoveredIcon
                        onClick={handleRemoveWatcher}
                        sx={{ padding: '8px', height: '100%' }}
                      >
                        <DeleteIconBig />
                      </StyledHoveredIcon>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
          {watchers.length > 0 && (
            <Grid
              container
              direction={'column'}
              rowSpacing={1}
              sx={{
                marginTop: '6px',
                lineHeight: '20px',
              }}
            >
              {watchers.map((watcher, index) => (
                <Grid key={index}>
                  <Grid
                    container
                    sx={{
                      alignItems: 'center',
                    }}
                  >
                    <Grid sx={{ width: '250px' }}>
                      <Typography
                        variant='h4'
                        sx={{
                          color: '#212346',
                        }}
                      >
                        {watcher.userName}
                      </Typography>
                    </Grid>
                    <Grid sx={{ display: 'inline-flex' }}>
                      <StyledHoveredIcon
                        onClick={() => handleDeleteIssueWatcher(watcher.userId)}
                        sx={{
                          padding: '0 8px',
                          height: '100%',
                          marginLeft: '10px',
                        }}
                      >
                        <DeleteIconBig />
                      </StyledHoveredIcon>
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}
    </>
  );
};

export default IssueWatchers;
